'use client';

import { Star, ChevronDown } from 'lucide-react';
import { DirectoryFiltersSheet } from './DirectoryFiltersSidebar';
import { DIRECTORY_TYPES, TYPE_COLORS, TYPE_ICONS, type DirectoryType } from '@/constants/directoryTypes';

interface FilterState {
  types: string[];
  instruments: string[];
  minRating: number | null;
  claimed: boolean | null;
  sortBy: string;
  cities: string[];
}

type TypeCount = Record<DirectoryType, number>;

interface CityOption {
  city: string;
  count: number;
  province: string;
}

const TYPE_CHIPS = [
  { value: 'school', label: 'Escuelas' },
  { value: 'rehearsal', label: 'Locales de ensayo' },
  { value: 'store', label: 'Tiendas' },
] as const satisfies ReadonlyArray<{ value: DirectoryType; label: string }>;

const RATING_CHIPS = [
  { value: 4.5, label: '4,5+' },
  { value: 4, label: '4+' },
];

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevancia' },
  { value: 'rating', label: 'Mejor valorados' },
  { value: 'views', label: 'Más vistos' },
  { value: 'recent', label: 'Más recientes' },
];

const ALL_TYPES = DIRECTORY_TYPES;

const chipBase =
  'flex-shrink-0 inline-flex h-9 items-center gap-1.5 rounded-xl border px-3.5 text-sm font-medium transition-all';
const chipInactive = 'border-gray-200 bg-white text-gray-700 hover:border-gray-300';

/**
 * Barra de filtros horizontal estilo GetYourGuide: [Filtros] · [Ordenar ▾] · chips de tipo
 * · chips de valoración. Sticky, scroll horizontal. El botón "Filtros" abre el Sheet con
 * lo avanzado (instrumentos, ciudades, verificados).
 */
export function DirectoryFiltersBar({
  filters,
  onFilterChange,
  totalResults,
  typeCounts,
  availableCities,
  editorial = false,
}: {
  filters: FilterState;
  onFilterChange: (f: FilterState) => void;
  totalResults: number;
  typeCounts?: TypeCount;
  availableCities?: CityOption[];
  editorial?: boolean;
}) {
  const activeType = filters.types.length === 1 ? filters.types[0] : null;

  function toggleType(value: string) {
    const isOnlyThis = activeType === value;
    onFilterChange({ ...filters, types: isOnlyThis ? [...ALL_TYPES] : [value] });
  }

  function toggleRating(value: number) {
    onFilterChange({ ...filters, minRating: filters.minRating === value ? null : value });
  }

  if (editorial) return <div className="directory-filter-inner">
    <div className="directory-filter-types" aria-label="Tipo de establecimiento">
      <button type="button" aria-pressed={filters.types.length === ALL_TYPES.length} onClick={() => onFilterChange({ ...filters, types: [...ALL_TYPES] })}>Todo</button>
      {TYPE_CHIPS.map(chip => { const Icon = TYPE_ICONS[chip.value]; return <button type="button" key={chip.value} aria-pressed={activeType === chip.value} onClick={() => toggleType(chip.value)}><Icon size={16}/>{chip.label}</button>; })}
    </div>
    <div className="directory-filter-actions">
      <label className="sr-only" htmlFor="directory-sort">Ordenar resultados</label>
      <select id="directory-sort" value={filters.sortBy} onChange={e => onFilterChange({ ...filters, sortBy: e.target.value })}>{SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
      <DirectoryFiltersSheet filters={filters} onFilterChange={onFilterChange} totalResults={totalResults} typeCounts={typeCounts} availableCities={availableCities}/>
    </div>
  </div>;

  return (
    <div className="bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 overflow-x-auto py-3 scrollbar-none">
          {/* Filtros (Sheet completo) */}
          <div className="flex-shrink-0">
            <DirectoryFiltersSheet
              filters={filters}
              onFilterChange={onFilterChange}
              totalResults={totalResults}
              typeCounts={typeCounts}
              availableCities={availableCities}
            />
          </div>

          {/* Ordenar */}
          <div className="relative flex-shrink-0">
            <select
              value={filters.sortBy}
              onChange={(e) => onFilterChange({ ...filters, sortBy: e.target.value })}
              className="h-9 cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white pl-3.5 pr-8 text-sm font-medium text-gray-700 hover:border-gray-300 focus:outline-none"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          </div>

          <div className="h-6 w-px flex-shrink-0 bg-gray-200" />

          {/* Chips de tipo */}
          {TYPE_CHIPS.map((chip) => {
            const Icon = TYPE_ICONS[chip.value];
            const isActive = activeType === chip.value;
            const count = typeCounts?.[chip.value as keyof TypeCount] ?? 0;
            return (
              <button
                key={chip.value}
                onClick={() => toggleType(chip.value)}
                className={`${chipBase} ${isActive ? 'border-transparent text-white' : chipInactive}`}
                style={isActive ? { backgroundColor: TYPE_COLORS[chip.value] } : undefined}
              >
                <Icon className="h-4 w-4" style={isActive ? undefined : { color: TYPE_COLORS[chip.value] }} />
                {chip.label}
                {count > 0 && <span className={isActive ? 'text-white/80' : 'text-gray-400'}>{count}</span>}
              </button>
            );
          })}

          <div className="h-6 w-px flex-shrink-0 bg-gray-200" />

          {/* Chips de valoración */}
          {RATING_CHIPS.map((r) => {
            const isActive = filters.minRating === r.value;
            return (
              <button
                key={r.value}
                onClick={() => toggleRating(r.value)}
                className={`${chipBase} ${isActive ? 'border-transparent bg-amber-500 text-white' : chipInactive}`}
              >
                <Star className={`h-3.5 w-3.5 ${isActive ? 'fill-white text-white' : 'fill-amber-400 text-amber-400'}`} />
                {r.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
